function csvCell(value) {
  return `"${String(value ?? "").replace(/"/g, '""')}"`;
}

export function exportDeepTestJson(report) {
  return JSON.stringify(report?.raw || report, null, 2);
}

export function exportDeepTestCsv(report) {
  if (!report) return "";
  const rows = [];

  rows.push(["Firestore Deep Test Report"]);
  rows.push(["Connection", csvCell(report.connectionName)]);
  rows.push(["Project", csvCell(report.projectId)]);
  rows.push(["Started", csvCell(report.startedAt)]);
  rows.push(["Completed", csvCell(report.completedAt)]);
  rows.push(["Duration (ms)", report.duration ?? ""]);
  rows.push(["Overall", csvCell(report.overall)]);
  rows.push([]);

  rows.push(["Scores"]);
  rows.push(["Health", report.healthScore ?? ""]);
  rows.push(["Performance", report.performanceScore ?? ""]);
  rows.push(["Permission", report.permissionScore ?? ""]);
  rows.push(["Security", report.securityScore ?? ""]);
  rows.push([]);

  rows.push(["Step", "Status", "Latency (ms)", "Message"]);
  (report.steps || []).forEach((s) => {
    rows.push([csvCell(s.label), s.status, s.latencyMs ?? "", csvCell(s.message)]);
  });
  rows.push([]);

  rows.push(["Permission", "Status", "Message"]);
  Object.entries(report.permissions || {}).forEach(([k, v]) => {
    rows.push([k, v.status, csvCell(v.message)]);
  });
  rows.push([]);

  rows.push(["Performance Metric", "Value (ms)"]);
  Object.entries(report.performance || {}).forEach(([k, v]) => {
    rows.push([k, v]);
  });
  rows.push([]);

  if (report.warnings?.length) {
    rows.push(["Warning Type", "Severity", "Message"]);
    report.warnings.forEach((w) => rows.push([w.type, w.severity, csvCell(w.message)]));
    rows.push([]);
  }

  rows.push(["Recommendation", csvCell(report.recommendation)]);

  return rows.map((r) => r.join(",")).join("\n");
}

export async function exportDeepTestPdf(report, filename = "deep-test.pdf") {
  const { jsPDF } = await import("jspdf");
  const docPdf = new jsPDF();
  let y = 16;

  const line = (text, size = 10, indent = 14) => {
    if (y > 275) { docPdf.addPage(); y = 16; }
    docPdf.setFontSize(size);
    const lines = docPdf.splitTextToSize(String(text), 180 - (indent - 14));
    docPdf.text(lines, indent, y);
    y += lines.length * (size * 0.5) + 2;
  };

  line("Firestore Deep Test Report", 16);
  y += 2;
  line(`Connection: ${report?.connectionName || "—"}`);
  line(`Project: ${report?.projectId || "—"}`);
  line(`Overall: ${report?.overall || "—"} · Duration: ${report?.duration ?? 0} ms`);
  line(`Completed: ${report?.completedAt || "—"}`);
  y += 2;

  line("Scores", 13);
  line(`Health ${report?.healthScore ?? 0}%  ·  Performance ${report?.performanceScore ?? 0}%  ·  Permission ${report?.permissionScore ?? 0}%  ·  Security ${report?.securityScore ?? 0}%`);
  y += 2;

  line("Steps", 13);
  (report?.steps || []).forEach((s) => {
    line(`${s.label}: ${s.status}${s.latencyMs ? ` (${s.latencyMs} ms)` : ""}`, 10, 16);
    if (s.message) line(s.message, 9, 20);
  });
  y += 2;

  line("Permissions", 13);
  Object.entries(report?.permissions || {}).forEach(([k, v]) => {
    line(`${k}: ${v.status} — ${v.message}`, 9, 16);
  });
  y += 2;

  if (report?.warnings?.length) {
    line("Warnings", 13);
    report.warnings.forEach((w) => line(`[${w.severity}] ${w.message}`, 9, 16));
    y += 2;
  }

  line("Recommendation", 13);
  line(report?.recommendation || "—", 10, 16);

  docPdf.save(filename);
}

export function formatDeepTestReport(report) {
  if (!report) return "";
  const lines = [];
  lines.push("Firestore Deep Test Report");
  lines.push(`Connection: ${report.connectionName || "—"} (${report.projectId || "—"})`);
  lines.push(`Overall: ${report.overall} · Duration: ${report.duration} ms`);
  lines.push(`Health ${report.healthScore}% · Performance ${report.performanceScore}% · Permission ${report.permissionScore}% · Security ${report.securityScore}%`);
  lines.push("");
  lines.push("Steps:");
  (report.steps || []).forEach((s) => {
    const icon =
      s.status === "success" ? "✔" :
      s.status === "skipped" ? "○" :
      s.status === "failed" ? "✘" : "⚠";
    lines.push(`  ${icon} ${s.label}: ${s.status}${s.latencyMs ? ` · ${s.latencyMs}ms` : ""}`);
    if (s.message) lines.push(`      ${s.message}`);
  });
  lines.push("");
  lines.push("Permissions:");
  Object.entries(report.permissions || {}).forEach(([k, v]) => {
    lines.push(`  ${k}: ${v.status} — ${v.message}`);
  });
  if (report.warnings?.length) {
    lines.push("");
    lines.push("Warnings:");
    report.warnings.forEach((w) => lines.push(`  • [${w.severity}] ${w.message}`));
  }
  lines.push("");
  lines.push(`Recommendation: ${report.recommendation || "—"}`);
  return lines.join("\n");
}
